/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * UNIFIED DOCUMENT VIEWER - Enterprise Citation Overlay System
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Supports:
 * - PDF with bounding box overlays
 * - Excel with cell range highlighting
 * - Word with paragraph highlighting
 * - PowerPoint with slide/shape highlighting
 *
 * Features:
 * - Scroll-to-citation
 * - Visual ghost highlights
 * - Click to navigate
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../../services/auth/AuthService';

import {
  FileText,
  FileSpreadsheet,
  Presentation,
  File,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
  Download,
  Loader2,
} from 'lucide-react';

// Types
interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Citation {
  citationId: string;
  sourceFile: {
    id: string;
    name: string;
    type: 'pdf' | 'excel' | 'word' | 'powerpoint' | 'document';
  };
  location: {
    pageNumber?: number;
    boundingBox?: BoundingBox;
    charStart?: number;
    charEnd?: number;
    slideNumber?: number;
    shapeId?: string;
    sheetName?: string;
    cellRange?: string;
    paragraphIndex?: number;
  };
  content: {
    exactText: string;
    contextWindow?: string;
  };
}

interface UnifiedDocumentViewerProps {
  fileId: string;
  fileUrl: string;
  fileType: 'pdf' | 'excel' | 'word' | 'powerpoint' | 'document';
  fileName: string;
  citations?: Citation[];
  activeCitation?: Citation | null;
  onCitationClick?: (citation: Citation) => void;
  onClose?: () => void;
}

// ✅ ENTERPRISE: Single token getter (Auth v2 only; avoids legacy signature mismatches)
const getAuthToken = (): string => {
  try {
    const token = authService?.getAccessToken?.() || '';
    const t = String(token || '').trim();
    const lower = t.toLowerCase();
    const isJwtLike = t.split('.').length >= 3;
    if (!t || lower === 'null' || lower === 'undefined' || !isJwtLike) return '';
    return t;
  } catch {
    return '';
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// PDF VIEWER WITH BBOX OVERLAY
// ═══════════════════════════════════════════════════════════════════════════════

const PDFViewer: React.FC<{
  fileUrl: string;
  citations: Citation[];
  activeCitation: Citation | null;
  onCitationClick: (citation: Citation) => void;
}> = ({ fileUrl, citations, activeCitation, onCitationClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pageRendering, setPageRendering] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Load PDF.js dynamically
  useEffect(() => {
    const loadPdfJs = async () => {
      // @ts-ignore
      if (!window.pdfjsLib) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          // @ts-ignore
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          loadDocument();
        };
        document.head.appendChild(script);
      } else {
        loadDocument();
      }
    };

    const loadDocument = async () => {
      try {
        // @ts-ignore
        const pdf = await window.pdfjsLib.getDocument(fileUrl).promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
      } catch (error) {
        console.error('Error loading PDF:', error);
      }
    };

    loadPdfJs();
  }, [fileUrl]);

  // Render page
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    const renderPage = async () => {
      setPageRendering(true);
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale: zoom });

      const canvas = canvasRef.current!;
      const context = canvas.getContext('2d')!;
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      setPageRendering(false);
    };

    renderPage();
  }, [pdfDoc, currentPage, zoom]);

  // Scroll to active citation
  useEffect(() => {
    if (activeCitation?.location?.pageNumber) {
      setCurrentPage(activeCitation.location.pageNumber);
    }
  }, [activeCitation]);

  // Get citations for current page
  const pageCitations = citations.filter(c => c.location?.pageNumber === currentPage);

  return (
    <div className="relative flex flex-col h-full bg-slate-100 dark:bg-slate-900">
      {/* Controls */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
            className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom(z => Math.min(3, z + 0.25))}
            className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* PDF Canvas with Overlay */}
      <div ref={containerRef} className="flex-1 overflow-auto p-4 flex justify-center">
        <div className="relative inline-block">
          {pageRendering && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 z-10">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          )}

          <canvas ref={canvasRef} className="shadow-lg" />

          {/* Citation Overlays */}
          <div
            ref={overlayRef}
            className="absolute inset-0 pointer-events-none"
            style={{ width: canvasRef.current?.width, height: canvasRef.current?.height }}
          >
            {pageCitations.map((citation, idx) => {
              const bbox = citation.location?.boundingBox;
              if (!bbox) return null;

              const isActive = activeCitation?.citationId === citation.citationId;

              return (
                <div
                  key={citation.citationId || idx}
                  className={`
                    absolute pointer-events-auto cursor-pointer
                    transition-all duration-200
                    ${
                      isActive
                        ? 'bg-yellow-300/50 border-2 border-yellow-500 shadow-lg'
                        : 'bg-blue-200/30 border border-blue-400/50 hover:bg-blue-300/40'
                    }
                  `}
                  style={{
                    left: bbox.x * zoom,
                    top: bbox.y * zoom,
                    width: bbox.width * zoom,
                    height: bbox.height * zoom,
                  }}
                  onClick={() => onCitationClick(citation)}
                  title={citation.content?.exactText?.slice(0, 100)}
                >
                  {isActive && (
                    <div className="absolute -top-6 left-0 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                      Citation {idx + 1}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EXCEL VIEWER WITH CELL HIGHLIGHTING
// ═══════════════════════════════════════════════════════════════════════════════

const ExcelViewer: React.FC<{
  fileUrl: string;
  citations: Citation[];
  activeCitation: Citation | null;
  onCitationClick: (citation: Citation) => void;
}> = ({ fileUrl, citations, activeCitation, onCitationClick }) => {
  const [data, setData] = useState<any[][]>([]);
  const [allSheets, setAllSheets] = useState<Record<string, any[][]>>({}); // ✅ Store all sheets
  const [sheets, setSheets] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState('');
  const [loading, setLoading] = useState(true);
  const tableRef = useRef<HTMLTableElement>(null);

  // Parse cell range like "B2:D10" into row/col bounds
  const parseCellRange = (
    range: string
  ): {
    startCol: number;
    startRow: number;
    endCol: number;
    endRow: number;
  } | null => {
    const match = range.match(/([A-Z]+)(\d+):?([A-Z]*)(\d*)/i);
    if (!match) return null;

    const colToNum = (col: string) => {
      let num = 0;
      for (let i = 0; i < col.length; i++) {
        num = num * 26 + col.charCodeAt(i) - 64;
      }
      return num - 1;
    };

    return {
      startCol: colToNum(match[1].toUpperCase()),
      startRow: parseInt(match[2]) - 1,
      endCol: match[3] ? colToNum(match[3].toUpperCase()) : colToNum(match[1].toUpperCase()),
      endRow: match[4] ? parseInt(match[4]) - 1 : parseInt(match[2]) - 1,
    };
  };

  // Check if cell is in citation range
  const isCellHighlighted = (rowIdx: number, colIdx: number): Citation | null => {
    for (const citation of citations) {
      if (citation.location?.sheetName === activeSheet && citation.location?.cellRange) {
        const range = parseCellRange(citation.location.cellRange);
        if (
          range &&
          rowIdx >= range.startRow &&
          rowIdx <= range.endRow &&
          colIdx >= range.startCol &&
          colIdx <= range.endCol
        ) {
          return citation;
        }
      }
    }
    return null;
  };

  // Load Excel data using the correct API endpoint
  useEffect(() => {
    const loadExcel = async () => {
      try {
        // ✅ FIX: fileUrl is the base path to the file, append /excel for sheet data
        // The endpoint is: /api/smart-notebook/notebooks/:notebookId/files/:fileId/excel
        const excelUrl = fileUrl.includes('http') ? `${fileUrl}/excel` : `${fileUrl}/excel`;

        console.log('[ExcelViewer] Loading from:', excelUrl);

        // Add auth token if available (Auth v2 compatible)
        const token = getAuthToken();
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        let response = await fetch(excelUrl, { headers });

        // If /excel fails, try /viewer-preview endpoint (JSON only)
        if (!response.ok) {
          const previewUrl = fileUrl.includes('http') ? `${fileUrl}/viewer-preview` : `${fileUrl}/viewer-preview`;
          console.log('[ExcelViewer] Trying fallback:', previewUrl);
          response = await fetch(previewUrl, { headers });
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error('Excel preview returned non-JSON payload');
        }

        const result = await response.json();
        console.log('[ExcelViewer] Loaded data:', result);

        // Handle response format: { sheetNames: [...], sheets: {...} }
        if (result.sheetNames && result.sheets) {
          setSheets(result.sheetNames);
          setAllSheets(result.sheets); // ✅ Store all sheets
          setActiveSheet(result.sheetNames[0]);
          setData(result.sheets[result.sheetNames[0]] || []);
        } else if (result.sheets) {
          // Alternative format: just sheets object
          const sheetNames = Object.keys(result.sheets);
          setSheets(sheetNames);
          setAllSheets(result.sheets); // ✅ Store all sheets
          setActiveSheet(sheetNames[0]);
          setData(result.sheets[sheetNames[0]] || []);
        } else {
          console.warn('[ExcelViewer] Unexpected response format:', result);
          setData([['No sheet data available']]);
        }
      } catch (error) {
        console.error('[ExcelViewer] Error loading Excel:', error);
        // Fallback: show placeholder with error
        setData([
          ['Could not load Excel preview'],
          ['Error: ' + (error as Error).message],
          ['Try using "Extracted Text" view'],
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadExcel();
  }, [fileUrl]);

  // ✅ FIX: Update data when activeSheet changes
  useEffect(() => {
    if (activeSheet && allSheets[activeSheet]) {
      setData(allSheets[activeSheet]);
      console.log(`[ExcelViewer] Switched to sheet: ${activeSheet}`);
    }
  }, [activeSheet, allSheets]);

  // Scroll to active citation
  useEffect(() => {
    if (activeCitation?.location?.sheetName) {
      setActiveSheet(activeCitation.location.sheetName);
    }
  }, [activeCitation]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      {/* Sheet Tabs - Compact horizontal scrollable */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 overflow-x-auto flex-shrink-0">
        {sheets.map(sheet => (
          <button
            key={sheet}
            onClick={() => setActiveSheet(sheet)}
            className={`
              px-2 py-1 text-xs rounded transition-colors whitespace-nowrap flex-shrink-0
              ${
                activeSheet === sheet
                  ? 'bg-white dark:bg-slate-900 text-green-600 dark:text-green-400 shadow-sm border border-slate-200 dark:border-slate-600'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }
            `}
          >
            {sheet}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-4">
        <table ref={tableRef} className="min-w-full border-collapse text-sm">
          <tbody>
            {data.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {/* Row number */}
                <td className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 text-xs font-mono border border-slate-200 dark:border-slate-700 sticky left-0">
                  {rowIdx + 1}
                </td>
                {Array.isArray(row) &&
                  row.map((cell, colIdx) => {
                    const citation = isCellHighlighted(rowIdx, colIdx);
                    const isActive = citation?.citationId === activeCitation?.citationId;

                    return (
                      <td
                        key={colIdx}
                        onClick={() => citation && onCitationClick(citation)}
                        className={`
                        px-2 py-1 border border-slate-200 dark:border-slate-700
                        ${
                          citation
                            ? isActive
                              ? 'bg-yellow-200 dark:bg-yellow-900/50 border-yellow-400 cursor-pointer'
                              : 'bg-green-100 dark:bg-green-900/30 border-green-300 cursor-pointer hover:bg-green-200'
                            : ''
                        }
                      `}
                      >
                        {cell?.toString() || ''}
                      </td>
                    );
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WORD VIEWER WITH PARAGRAPH HIGHLIGHTING
// ═══════════════════════════════════════════════════════════════════════════════

const WordViewer: React.FC<{
  fileUrl: string;
  citations: Citation[];
  activeCitation: Citation | null;
  onCitationClick: (citation: Citation) => void;
}> = ({ fileUrl, citations, activeCitation, onCitationClick }) => {
  const [content, setContent] = useState<string>('');
  const [paragraphs, setParagraphs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        // ✅ FIX: Use the /word endpoint for Word documents
        const wordUrl = `${fileUrl}/word`;
        console.log('[WordViewer] Loading from:', wordUrl);

        const token = getAuthToken();
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        let response = await fetch(wordUrl, { headers });

        // Fallback to /preview if /word doesn't exist
        if (!response.ok) {
          const previewUrl = `${fileUrl}/preview`;
          console.log('[WordViewer] Trying fallback:', previewUrl);
          response = await fetch(previewUrl, { headers });
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        console.log('[WordViewer] Loaded:', result);

        if (result.html) {
          setContent(result.html);
          // Parse paragraphs from response or from HTML
          if (result.paragraphs) {
            setParagraphs(result.paragraphs.map((p: any) => p.text || p));
          } else {
            const parser = new DOMParser();
            const doc = parser.parseFromString(result.html, 'text/html');
            const paras = Array.from(doc.querySelectorAll('p')).map(p => p.textContent || '');
            setParagraphs(paras);
          }
        } else if (result.content || result.text) {
          // Fallback: use plain text content
          const text = result.content || result.text;
          setContent(`<div class="prose">${text.replace(/\n/g, '<br/>')}</div>`);
          setParagraphs(text.split('\n').filter((p: string) => p.trim()));
        }
      } catch (error) {
        console.error('[WordViewer] Error loading Word document:', error);
        setContent(
          '<div class="text-red-500">Could not load document preview. Try "Extracted Text" view.</div>'
        );
      } finally {
        setLoading(false);
      }
    };
    loadDocument();
  }, [fileUrl]);

  // Scroll to active citation paragraph
  useEffect(() => {
    if (activeCitation?.location?.paragraphIndex !== undefined && containerRef.current) {
      const para = containerRef.current.querySelector(
        `[data-paragraph="${activeCitation.location.paragraphIndex}"]`
      );
      para?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeCitation]);

  const getCitationForParagraph = (idx: number): Citation | null => {
    return citations.find(c => c.location?.paragraphIndex === idx) || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full overflow-auto p-8 bg-white dark:bg-slate-900">
      <div className="max-w-3xl mx-auto prose dark:prose-invert">
        {paragraphs.map((para, idx) => {
          const citation = getCitationForParagraph(idx);
          const isActive = citation?.citationId === activeCitation?.citationId;

          return (
            <p
              key={idx}
              data-paragraph={idx}
              onClick={() => citation && onCitationClick(citation)}
              className={`
                relative transition-all duration-200 rounded px-2 -mx-2
                ${
                  citation
                    ? isActive
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500 cursor-pointer'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-300 cursor-pointer hover:bg-blue-100'
                    : ''
                }
              `}
            >
              {para}
              {citation && isActive && (
                <span className="absolute -right-2 top-0 bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded">
                  Cited
                </span>
              )}
            </p>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// POWERPOINT VIEWER WITH SLIDE/SHAPE HIGHLIGHTING
// ═══════════════════════════════════════════════════════════════════════════════

const PowerPointViewer: React.FC<{
  fileUrl: string;
  citations: Citation[];
  activeCitation: Citation | null;
  onCitationClick: (citation: Citation) => void;
}> = ({ fileUrl, citations, activeCitation, onCitationClick }) => {
  const [slides, setSlides] = useState<{ imageUrl: string; shapes: any[] }[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPresentation = async () => {
      try {
        // ✅ FIX: Use the correct URL format with auth
        const slidesUrl = `${fileUrl}/slides`;
        console.log('[PowerPointViewer] Loading from:', slidesUrl);

        const token = getAuthToken();
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        let response = await fetch(slidesUrl, { headers });

        // Fallback to preview if slides endpoint doesn't exist
        if (!response.ok) {
          const previewUrl = `${fileUrl}/preview`;
          console.log('[PowerPointViewer] Trying fallback:', previewUrl);
          response = await fetch(previewUrl, { headers });
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        console.log('[PowerPointViewer] Loaded:', result);

        if (result.slides) {
          setSlides(result.slides);
        } else {
          // Fallback: create placeholder slides
          setSlides([{ imageUrl: '', shapes: [] }]);
        }
      } catch (error) {
        console.error('[PowerPointViewer] Error loading PowerPoint:', error);
        setSlides([{ imageUrl: '', shapes: [] }]);
      } finally {
        setLoading(false);
      }
    };
    loadPresentation();
  }, [fileUrl]);

  // Navigate to cited slide
  useEffect(() => {
    if (activeCitation?.location?.slideNumber) {
      setCurrentSlide(activeCitation.location.slideNumber - 1);
    }
  }, [activeCitation]);

  const slideCitations = citations.filter(c => c.location?.slideNumber === currentSlide + 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-800">
      {/* Slide Navigation */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white">
        <button
          onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
          disabled={currentSlide <= 0}
          className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-50"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="text-sm">
          Slide {currentSlide + 1} of {slides.length}
        </span>
        <button
          onClick={() => setCurrentSlide(s => Math.min(slides.length - 1, s + 1))}
          disabled={currentSlide >= slides.length - 1}
          className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-50"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Slide View */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div
          className="relative bg-white shadow-2xl"
          style={{ aspectRatio: '16/9', maxHeight: '70vh' }}
        >
          {slides[currentSlide]?.imageUrl ? (
            <img
              src={slides[currentSlide].imageUrl}
              alt={`Slide ${currentSlide + 1}`}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <Presentation className="h-16 w-16" />
            </div>
          )}

          {/* Shape Overlays */}
          {slideCitations.map((citation, idx) => {
            const isActive = citation.citationId === activeCitation?.citationId;
            // Would need shape position from metadata
            return (
              <div
                key={citation.citationId || idx}
                className={`
                  absolute inset-0 transition-all duration-200
                  ${
                    isActive
                      ? 'border-4 border-orange-500 bg-orange-500/10'
                      : 'border-2 border-orange-300/50 hover:border-orange-400'
                  }
                `}
                onClick={() => onCitationClick(citation)}
              />
            );
          })}
        </div>
      </div>

      {/* Slide Thumbnails */}
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 overflow-x-auto">
        {slides.map((slide, idx) => {
          const hasCitation = citations.some(c => c.location?.slideNumber === idx + 1);
          const isActive = idx === currentSlide;

          return (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`
                relative flex-shrink-0 w-24 h-14 rounded overflow-hidden border-2
                ${isActive ? 'border-white' : 'border-transparent'}
                ${hasCitation ? 'ring-2 ring-orange-500' : ''}
              `}
            >
              {slide.imageUrl ? (
                <img src={slide.imageUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-slate-700 flex items-center justify-center text-xs text-slate-400">
                  {idx + 1}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN UNIFIED VIEWER
// ═══════════════════════════════════════════════════════════════════════════════

export const UnifiedDocumentViewer: React.FC<
  UnifiedDocumentViewerProps & { hideHeader?: boolean }
> = ({
  fileId,
  fileUrl,
  fileType,
  fileName,
  citations = [],
  activeCitation = null,
  onCitationClick = () => {},
  onClose,
  hideHeader = false, // ✅ NEW: Hide header when used inside FilePreviewContent
}) => {
  const fileCitations = citations.filter(c => c.sourceFile?.id === fileId);

  const renderViewer = () => {
    switch (fileType) {
      case 'pdf':
        return (
          <PDFViewer
            fileUrl={fileUrl}
            citations={fileCitations}
            activeCitation={activeCitation}
            onCitationClick={onCitationClick}
          />
        );
      case 'excel':
        return (
          <ExcelViewer
            fileUrl={fileUrl}
            citations={fileCitations}
            activeCitation={activeCitation}
            onCitationClick={onCitationClick}
          />
        );
      case 'word':
        return (
          <WordViewer
            fileUrl={fileUrl}
            citations={fileCitations}
            activeCitation={activeCitation}
            onCitationClick={onCitationClick}
          />
        );
      case 'powerpoint':
        return (
          <PowerPointViewer
            fileUrl={fileUrl}
            citations={fileCitations}
            activeCitation={activeCitation}
            onCitationClick={onCitationClick}
          />
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <File className="h-16 w-16 mb-4" />
            <p>Preview not available for this file type</p>
          </div>
        );
    }
  };

  const getFileIcon = () => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="h-5 w-5 text-red-500" />;
      case 'excel':
        return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
      case 'word':
        return <FileText className="h-5 w-5 text-blue-500" />;
      case 'powerpoint':
        return <Presentation className="h-5 w-5 text-orange-500" />;
      default:
        return <File className="h-5 w-5" />;
    }
  };

  return (
    <div
      className={`flex flex-col h-full bg-white dark:bg-slate-900 ${hideHeader ? '' : 'rounded-lg shadow-xl'} overflow-hidden`}
    >
      {/* Header - hidden when used inside FilePreviewContent */}
      {!hideHeader && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          <div className="flex items-center gap-3">
            {getFileIcon()}
            <span className="font-medium text-slate-700 dark:text-slate-200 truncate max-w-md">
              {fileName}
            </span>
            {fileCitations.length > 0 && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-full">
                {fileCitations.length} citations
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
              <Download className="h-4 w-4" />
            </button>
            <button className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
              <Maximize2 className="h-4 w-4" />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Viewer */}
      <div className="flex-1 overflow-hidden">{renderViewer()}</div>
    </div>
  );
};

export default UnifiedDocumentViewer;
